import React, { Component, useEffect } from 'react';
import {
  BrowserRouter as Router, Link,
  Redirect,
  Route,
  Switch,
  withRouter,
} from "react-router-dom";
import { createGlobalStyle, css } from 'styled-components';
import Login from './routes/Login';
import App from './App';
import JwtStore from "./flux/JwtStore";
import DesktopView from "./routes/DesktopView";
import { ThemeProvider } from 'styled-components';
import withResponsive from './hoc/withResponsive';
import themeResponsiveProperty
  from './components/responsive/themeResponsiveProperty';
import isInDesktopView from './utils/isInDesktopView';
import RouterPathController from "./components/RouterPathController";
import theme from "./theme";
import LoginCallback from "./routes/LoginCallback";
import shouldRedirectToDesktopView from "./utils/shouldRedirectToDesktopView";
import Logout from "./routes/Logout";
import ConfirmAuthenticationProfile from "./routes/ConfirmAuthenticationProfile";

class AppRouter extends Component {
  state = {
    hasToken: JwtStore.getToken() !== null,
    didConfirmAuthentication: JwtStore.didConfirmAuthentication(),
    loginFailed: false,
    brands: JwtStore.getBrands(),
    deepLink: null,
  };

  subscriptions = [];

  handlePostMessage = (event) => {
    let message;

    try {
      message = JSON.parse(event.data);
    } catch (error) {
      return;
    }

    switch (message.name) {
      case 'native-path-change':
        this.setState({ deepLink: message.data.uri });
        return;

      case 'pre-authenticate-result':
        if (message.data.success === true) {
          window.localStorage.setItem('enrolled-in-biometric-auth', '1');
        } else {
          window.localStorage.setItem('enrolled-in-biometric-auth', '0');
        }
        return;
    }
  }

  componentDidMount() {
    this.subscriptions.push(JwtStore.addListener(() => {
      setTimeout(() => this.setState({
        hasToken: JwtStore.getToken() !== null,
        didConfirmAuthentication: JwtStore.didConfirmAuthentication(),
        loginFailed: JwtStore.getState().loginFailed,
        brands: JwtStore.getBrands(),
      }),0);
    }));

    window.addEventListener("message", this.handlePostMessage, false);
  };

  componentWillUnmount() {
    this.subscriptions.forEach(subscription => subscription.remove());

    window.removeEventListener("message", this.handlePostMessage);
  }

  render() {
    const search = new URLSearchParams(window.location.search);

    let contextVersion = search.get('contextVersion');

    if (contextVersion === null) {
      contextVersion = window.localStorage.getItem('contextVersion');
    } else {
      window.localStorage.setItem('contextVersion', contextVersion);
    }

    // If the application is inside a ReactNativeWebView (thus wrapped in an
    // iOS application), we expect to find a contextVersion query parameter.
    // Otherwise, it's an old IPA and we redirect to a maintenance page.
    if (
      window.ReactNativeWebView !== undefined &&
      contextVersion === null
    ) {
      window.location.replace(process.env.REACT_APP_AUTHENTICATION_DOMAIN + '/maintenance/migration-page-outdated-version.html')
    }

    return (
      <ThemeProvider theme={{
        ...theme,
        isMobile: this.props.isMobile,
        isTablet: this.props.isTablet,
      }}>
        <GlobalStyle isInDesktopView={isInDesktopView()} />

        {this.state.hasToken && (
          <RedirectToIntended />
        )}

        <Router>
          <Route path="/" render={() => (
            <RouterPathController
              path={this.state.deepLink}
              onPush={() => this.setState({
                deepLink: null
              })}
            />
          )} />

          <Switch>
            {this.state.hasToken && (
              // we do not allow performing the login flow through the Desktop
              // view
              <Route exact path="/desktop" component={DesktopView} />
            )}

            <Route exact path="/logout" component={Logout} />

            {this.state.hasToken ? (
              <Switch>
                {(
                  // We do not care to have a desktop frame when not logged-in,
                  // it also causes issues with the auth redirects
                  isInDesktopView() === false &&
                  shouldRedirectToDesktopView()
                ) && (
                  <RedirectRememberIntended to="/desktop" />
                )}

                {(
                  this.state.didConfirmAuthentication === false &&
                  isInDesktopView() === true &&
                  this.props.isTablet
                ) ? (
                  <Route path="/" render={() => (
                    <ConfirmAuthenticationProfile />
                  )} />
                ) : (
                  <Route path="/" component={App} />
                )}

              </Switch>
            ) : (
              <Switch>
                <Route exact path="/login" component={Login} />
                <Route exact path="/login-callback" component={LoginCallback} />

                {isInDesktopView() ? (
                  <Redirect to="/login" />
                ) : (
                  <RedirectRememberIntended to="/login" />
                )}
              </Switch>
            )}
          </Switch>
        </Router>
      </ThemeProvider>
    );
  }
}

export default withResponsive(AppRouter);

export const GlobalStyle = createGlobalStyle`
  html,
  body {
    user-select: ${props => props.isInDesktopView ? 'default' : 'none'};
    font-family: ${props => props.theme.fontFamily};
    letter-spacing: ${themeResponsiveProperty(
      props => props.theme.fontFamilyLetterSpacing[0],
      props => props.theme.fontFamilyLetterSpacing[1]
    )}

    ${props => props.isInDesktopView && css`
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    `};
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 200;
    font-family: ${props => props.theme.fontFamilyHeading};
    letter-spacing: ${themeResponsiveProperty(
      props => props.theme.fontFamilyHeadingLetterSpacing[0],
      props => props.theme.fontFamilyHeadingLetterSpacing[1]
    )};
  }
`;

const RedirectRememberIntended = withRouter(function({ match, location, history, ...props }) {
  useEffect(function() {
    const intendedPath = location.pathname === '/'
      ? '/hot-now'
      : location.pathname + location.search;

    window.localStorage.setItem('intended-path', intendedPath);
  }, []);

  return <Redirect {...props} />
})

function RedirectToIntended() {
  useEffect(() => {
    const intendedPath = window.localStorage.getItem('intended-path');

    if (intendedPath === null) {
      return;
    }

    window.localStorage.removeItem('intended-path')

    window.postMessage(JSON.stringify({
      name: 'native-path-change',
      data: { uri: intendedPath }
    }))
  }, []);

  return null;
}
